/**
 * v11.0.116+ honest scan progress — global accumulators for v153 per-source interleaved scans.
 * Shared by libraryScanCoordinator and LibraryManagement progress handlers.
 */

export type ScanSetAsideSummary = {
  unreadable?: number;
  tag_error?: number;
  timeout?: number;
  permission?: number;
  other?: number;
};

export type ScanAggregatePhase = 'walking' | 'reading_tags' | 'finishing' | 'idle';

/** Per-source slot accumulation vs connector-emitted cumulative globals (overlap era). */
export type ScanProgressMode = 'unknown' | 'per_source' | 'global_direct';

export type HonestScanMetrics = {
  walkFilesFound: number | null;
  terminalFiles: number | null;
  pendingJobs: number | null;
  setAsideTotal: number;
  indexedCount: number | null;
  unchangedCount: number | null;
  currentPath: string | null;
  hasHonestDenominator: boolean;
  /** Aggregated NAS walk count across folders (never a single-folder bounce). */
  walkRunningTotal: number | null;
  /** Running sum of per-source walk_files_found as sources complete walk. */
  globalWalkFilesFound: number | null;
  /** Running sum of terminal_files across all known sources. */
  globalTerminalFiles: number | null;
  sourcesTotal: number | null;
  sourcesCompleted: number | null;
  activeSourceIndex: number | null;
  aggregatePhase: ScanAggregatePhase;
  aggregateStatusLine: string | null;
  interleavedMultiSource: boolean;
  smoothedTagFps: number | null;
  globalEtaSec: number | null;
  /** True when using connector cumulative walk_files_found / terminal_files directly. */
  globalDirectTotals?: boolean;
  /** Walk phase still discovering files — global walk total is provisional. */
  walkStillRunning?: boolean;
  /** ETA suppressed while denominator may still grow. */
  globalEtaEstimating?: boolean;
};

export type ScanPhaseKind = 'walking' | 'reading_tags' | 'finishing' | 'other';

type SourceSlot = {
  walkFiles: number;
  terminalFiles: number;
  tagComplete: boolean;
};

type GlobalScanState = {
  sourcesTotal: number;
  sourceSlots: Map<number, SourceSlot>;
  lastWalkSourceIndex: number;
  activeTagSourceIndex: number;
  hasSeenTagRead: boolean;
  smoothedTagFps: number;
  lastTagSample: { terminal: number; t: number } | null;
  smoothedEtaSec: number | null;
  monotonicProgress: number;
  aggregatePhase: ScanAggregatePhase;
  progressMode: ScanProgressMode;
  /** Largest walk_files_found seen on a single event (sanity clamp reference). */
  peakEventGlobalWalk: number;
  lastDirectGlobalWalk: number;
  lastDirectGlobalTerminal: number;
  prevDirectGlobalWalk: number;
  globalWalkGrowing: boolean;
  lastWalkGrowthAt: number;
  walkAllFoldersComplete: boolean;
};

const PROVISIONAL_PROGRESS_CAP = 0.95;
const WALK_GROWTH_QUIET_MS = 20_000;

const globalScanByOp = new Map<string, GlobalScanState>();

/** Contract v1 present on connector scan-progress events (v11.0.127+). */
export function hasScanProgressContract(p: Record<string, unknown>): boolean {
  return p.contract_version === 1;
}

/** True when the connector emitted an explicit terminal scan event. */
export function isScanProgressTerminalEvent(p: Record<string, unknown>): boolean {
  if (p.terminal !== true) return false;
  const phase = String(p.phase ?? '');
  return phase === 'scan_complete' || phase === 'cancelled' || phase === 'error';
}

export function scanModeFromProgress(p: Record<string, unknown>): 'incremental' | 'cold' | null {
  const mode = p.scan_mode;
  if (mode === 'incremental' || mode === 'cold') return mode;
  return null;
}

type WalkAggregateState = {
  completedSum: number;
  activeSourceIndex: number;
  activeFolderPeak: number;
  lastSealedSourceIndex: number;
};

const walkAggregateByOp = new Map<string, WalkAggregateState>();
/** Monotonic albums_identified peak per scan op — never regresses within a run. */
const albumsIdentifiedPeakByOp = new Map<string, number>();

function getGlobalScanState(opId: string): GlobalScanState {
  let state = globalScanByOp.get(opId);
  if (!state) {
    state = {
      sourcesTotal: 0,
      sourceSlots: new Map(),
      lastWalkSourceIndex: 0,
      activeTagSourceIndex: 0,
      hasSeenTagRead: false,
      smoothedTagFps: 0,
      lastTagSample: null,
      smoothedEtaSec: null,
      monotonicProgress: 0,
      aggregatePhase: 'idle',
      progressMode: 'unknown',
      peakEventGlobalWalk: 0,
      lastDirectGlobalWalk: 0,
      lastDirectGlobalTerminal: 0,
      prevDirectGlobalWalk: 0,
      globalWalkGrowing: false,
      lastWalkGrowthAt: 0,
      walkAllFoldersComplete: false,
    };
    globalScanByOp.set(opId, state);
  }
  return state;
}

export function resetHonestScanProgressMonotonic(opId?: string): void {
  if (opId) {
    globalScanByOp.delete(opId);
    walkAggregateByOp.delete(opId);
    albumsIdentifiedPeakByOp.delete(opId);
  } else {
    globalScanByOp.clear();
    walkAggregateByOp.clear();
    albumsIdentifiedPeakByOp.clear();
  }
}

/** Tracks max albums_identified seen during a scan — mirrors walkRunningTotal for tracks. */
export function updateAlbumsIdentifiedPeak(
  opId: string,
  p: Record<string, unknown>,
  floor = 0,
): number {
  const raw = typeof p.albums_identified === 'number' && p.albums_identified >= 0
    ? p.albums_identified
    : 0;
  const prev = albumsIdentifiedPeakByOp.get(opId) ?? floor;
  const next = Math.max(prev, raw, floor);
  albumsIdentifiedPeakByOp.set(opId, next);
  return next;
}

export function getAlbumsIdentifiedPeak(opId: string): number {
  return albumsIdentifiedPeakByOp.get(opId) ?? 0;
}

/** Tracks found during NAS walk — prefers connector audio_files heartbeat. */
export function walkTracksFoundFromProgress(p: Record<string, unknown>): number | null {
  const phase = String(p.phase ?? '');
  if (
    phase !== 'walk'
    && phase !== 'enumerating'
    && phase !== 'discovering'
    && phase !== 'reconciling'
  ) {
    return null;
  }
  if (typeof p.audio_files === 'number' && p.audio_files > 0) return p.audio_files;
  if (typeof p.entries_seen === 'number' && p.entries_seen > 0) return p.entries_seen;
  if (typeof p.current_file === 'number' && p.current_file > 0) return p.current_file;
  return null;
}

function getWalkAggregate(opId: string): WalkAggregateState {
  let state = walkAggregateByOp.get(opId);
  if (!state) {
    state = {
      completedSum: 0,
      activeSourceIndex: 0,
      activeFolderPeak: 0,
      lastSealedSourceIndex: 0,
    };
    walkAggregateByOp.set(opId, state);
  }
  return state;
}

function resolveWalkSourceIndex(
  p: Record<string, unknown>,
  sourcesTotal: number,
): number | null {
  const idx = typeof p.source_index === 'number' ? p.source_index : null;
  if (idx == null || idx < 1) return null;
  if (sourcesTotal > 0 && idx > sourcesTotal) return null;
  return idx;
}

/** Tag-read events may send sources_total as source_index — do not trust that field. */
function resolveTagSourceIndex(
  p: Record<string, unknown>,
  state: GlobalScanState,
  sourcesTotal: number,
): number | null {
  const idx = typeof p.source_index === 'number' ? p.source_index : null;
  if (idx != null && sourcesTotal > 0 && idx === sourcesTotal) {
    return state.activeTagSourceIndex || state.lastWalkSourceIndex || null;
  }
  if (idx != null && idx >= 1 && (sourcesTotal <= 0 || idx <= sourcesTotal)) return idx;
  return state.activeTagSourceIndex || state.lastWalkSourceIndex || null;
}

function perSourceWalkCount(p: Record<string, unknown>): number | null {
  if (typeof p.walk_files_found === 'number' && p.walk_files_found >= 0) {
    return p.walk_files_found;
  }
  if (typeof p.total_files === 'number' && p.total_files > 0) return p.total_files;
  if (typeof p.audio_files === 'number' && p.audio_files > 0) return p.audio_files;
  if (typeof p.current_file === 'number' && p.current_file > 0) return p.current_file;
  return null;
}

function eventDirectGlobalCounts(p: Record<string, unknown>): {
  walk: number | null;
  terminal: number | null;
} {
  const walk = typeof p.walk_files_found === 'number' && p.walk_files_found >= 0
    ? p.walk_files_found
    : null;
  const terminal = typeof p.terminal_files === 'number' && p.terminal_files >= 0
    ? p.terminal_files
    : (typeof p.current_file === 'number' && p.current_file >= 0 ? p.current_file : null);
  return { walk, terminal };
}

const GLOBAL_TOTALS_SANITY_MULTIPLIER = 3;

/**
 * Overlap-era connector emits cumulative library-wide walk_files_found / terminal_files
 * (local_core_adapter emit_tag_read_progress: source_index = sources_total).
 * Older connectors emit per-source counts — accumulate those in sourceSlots.
 */
export function isOverlapGlobalTotalsShape(
  p: Record<string, unknown>,
  sourcesTotal: number,
): boolean {
  const phase = String(p.phase ?? '');
  const isTagLike = phase === 'tag_read' || phase === 'scanning';
  if (!isTagLike) return false;
  const sourceIndex = typeof p.source_index === 'number' ? p.source_index : null;
  if (sourcesTotal > 1 && sourceIndex === sourcesTotal) return true;
  if (typeof p.parallel_workers === 'number') return true;
  return false;
}

function isWalkLikePhase(phase: string): boolean {
  return phase === 'walk'
    || phase === 'enumerating'
    || phase === 'discovering'
    || phase === 'reconciling';
}

/** True while NAS walk may still add files to the global total (overlap-era). */
function isWalkStillRunning(state: GlobalScanState, phase: string): boolean {
  if (state.walkAllFoldersComplete) return false;
  const sourcesTotal = state.sourcesTotal;
  if (sourcesTotal > 1 && state.lastWalkSourceIndex < sourcesTotal) return true;
  if (state.globalWalkGrowing) return true;
  if (isWalkLikePhase(phase)) return true;
  return false;
}

function noteGlobalWalkGrowth(state: GlobalScanState, walk: number): void {
  if (walk <= 0) return;
  if (walk > state.prevDirectGlobalWalk) {
    state.globalWalkGrowing = true;
    state.lastWalkGrowthAt = Date.now();
    state.walkAllFoldersComplete = false;
  } else if (
    state.globalWalkGrowing
    && state.lastWalkGrowthAt > 0
    && Date.now() - state.lastWalkGrowthAt > WALK_GROWTH_QUIET_MS
  ) {
    state.globalWalkGrowing = false;
  }
  state.prevDirectGlobalWalk = Math.max(state.prevDirectGlobalWalk, walk);
}

function resolveGlobalTotals(
  state: GlobalScanState,
  p: Record<string, unknown>,
): { walk: number; terminal: number } {
  const direct = eventDirectGlobalCounts(p);
  if (direct.walk != null) {
    state.peakEventGlobalWalk = Math.max(state.peakEventGlobalWalk, direct.walk);
  }

  const slotWalk = sumGlobalWalk(state);
  const slotTerminal = sumGlobalTerminal(state);

  if (isOverlapGlobalTotalsShape(p, state.sourcesTotal)) {
    state.progressMode = 'global_direct';
  }

  if (state.progressMode === 'global_direct') {
    if (direct.walk != null) {
      state.lastDirectGlobalWalk = Math.max(state.lastDirectGlobalWalk, direct.walk);
      noteGlobalWalkGrowth(state, state.lastDirectGlobalWalk);
    }
    if (direct.terminal != null) {
      state.lastDirectGlobalTerminal = Math.max(state.lastDirectGlobalTerminal, direct.terminal);
    }
    return {
      walk: state.lastDirectGlobalWalk,
      terminal: state.lastDirectGlobalTerminal,
    };
  }

  if (state.progressMode === 'unknown') {
    state.progressMode = 'per_source';
  }

  let walk = slotWalk;
  let terminal = slotTerminal;

  const peak = state.peakEventGlobalWalk;
  if (peak > 0 && walk > peak * GLOBAL_TOTALS_SANITY_MULTIPLIER) {
    state.progressMode = 'global_direct';
    if (direct.walk != null) {
      state.lastDirectGlobalWalk = Math.max(state.lastDirectGlobalWalk, direct.walk);
    } else {
      state.lastDirectGlobalWalk = Math.max(state.lastDirectGlobalWalk, peak);
    }
    if (direct.terminal != null) {
      state.lastDirectGlobalTerminal = Math.max(state.lastDirectGlobalTerminal, direct.terminal);
    }
    walk = state.lastDirectGlobalWalk;
    terminal = state.lastDirectGlobalTerminal;
  }

  return { walk, terminal };
}

function sumGlobalWalk(state: GlobalScanState): number {
  let sum = 0;
  state.sourceSlots.forEach((slot) => { sum += slot.walkFiles; });
  return sum;
}

function sumGlobalTerminal(state: GlobalScanState): number {
  let sum = 0;
  state.sourceSlots.forEach((slot) => { sum += slot.terminalFiles; });
  return sum;
}

function countSourcesCompleted(state: GlobalScanState): number {
  let n = 0;
  state.sourceSlots.forEach((slot) => { if (slot.tagComplete) n += 1; });
  return n;
}

function updateSmoothedTagFps(state: GlobalScanState, globalTerminal: number): void {
  const now = Date.now();
  const prev = state.lastTagSample;
  if (prev && globalTerminal > prev.terminal) {
    const dt = (now - prev.t) / 1000;
    const dc = globalTerminal - prev.terminal;
    if (dt >= 0.5 && dc > 0) {
      const instant = dc / dt;
      state.smoothedTagFps = state.smoothedTagFps > 0
        ? state.smoothedTagFps * 0.65 + instant * 0.35
        : instant;
    }
  }
  state.lastTagSample = { terminal: globalTerminal, t: now };
}

function estimateInstantGlobalEtaSec(
  state: GlobalScanState,
  globalWalk: number,
  globalTerminal: number,
  walkStillRunning: boolean,
  sourcesCompleted: number,
): number | null {
  const remainingFiles = Math.max(0, globalWalk - globalTerminal);

  if (walkStillRunning) {
    if (remainingFiles <= 0 || state.globalWalkGrowing) return null;
    const foldersLeft = Math.max(0, state.sourcesTotal - state.lastWalkSourceIndex);
    if (foldersLeft > 0 && globalWalk > 0 && remainingFiles < globalWalk * 0.1) {
      return null;
    }
  }

  if (remainingFiles <= 0) {
    return walkStillRunning ? null : 0;
  }

  if (state.smoothedTagFps > 0.05) {
    const sec = remainingFiles / state.smoothedTagFps;
    if (walkStillRunning && sec < 30 && state.lastWalkSourceIndex < state.sourcesTotal) {
      return null;
    }
    return Math.round(sec);
  }

  if (state.progressMode === 'global_direct' && walkStillRunning) {
    return null;
  }

  const sourcesTotal = state.sourcesTotal;
  const sourcesRemaining = Math.max(0, sourcesTotal - sourcesCompleted);
  if (sourcesRemaining > 0) {
    return Math.round(sourcesRemaining * 4);
  }

  return null;
}

function isGlobalEtaMisleading(
  state: GlobalScanState,
  globalWalk: number,
  globalTerminal: number,
  walkStillRunning: boolean,
  smoothedEtaSec: number | null,
): boolean {
  if (!walkStillRunning) return false;
  const remaining = Math.max(0, globalWalk - globalTerminal);
  if (remaining <= 0 && (state.globalWalkGrowing || !state.walkAllFoldersComplete)) return true;
  if (smoothedEtaSec != null && smoothedEtaSec <= 2 && remaining > 100) return true;
  return false;
}

function smoothEta(
  state: GlobalScanState,
  instantSec: number | null,
  walkStillRunning: boolean,
): number | null {
  if (instantSec == null) {
    if (walkStillRunning && state.smoothedEtaSec != null && state.smoothedEtaSec < 120) {
      state.smoothedEtaSec = null;
    }
    return state.smoothedEtaSec;
  }
  if (walkStillRunning && state.smoothedEtaSec != null && instantSec > state.smoothedEtaSec * 1.5) {
    state.smoothedEtaSec = Math.round(state.smoothedEtaSec * 0.45 + instantSec * 0.55);
  } else {
    state.smoothedEtaSec = state.smoothedEtaSec != null
      ? Math.round(state.smoothedEtaSec * 0.72 + instantSec * 0.28)
      : instantSec;
  }
  return state.smoothedEtaSec;
}

function computeProgressRatio(
  state: GlobalScanState,
  phase: string,
  globalWalk: number,
  globalTerminal: number,
  sourcesCompleted: number,
  walkStillRunning: boolean,
): number {
  const sourcesTotal = state.sourcesTotal;
  const interleaved = sourcesTotal > 1;
  const globalDirect = state.progressMode === 'global_direct';

  if (phase === 'scan_complete' || phase === 'done') return 1;

  if (phase === 'finalize' || phase === 'finalizing') {
    return walkStillRunning ? PROVISIONAL_PROGRESS_CAP : 1;
  }

  let raw = 0;

  if (globalDirect && globalWalk > 0) {
    raw = globalTerminal / globalWalk;
  } else if (interleaved && sourcesTotal > 0) {
    let within = 0;
    const activeIdx = state.activeTagSourceIndex;
    if (activeIdx > 0) {
      const slot = state.sourceSlots.get(activeIdx);
      if (slot && slot.walkFiles > 0 && !slot.tagComplete) {
        within = Math.min(1, slot.terminalFiles / slot.walkFiles);
      }
    }
    if (within === 0 && isWalkLikePhase(phase)) {
      within = 0.12;
    }
    raw = Math.min(1, (sourcesCompleted + within) / sourcesTotal);
    if (globalWalk > 0) {
      const fileRatio = Math.min(1, globalTerminal / globalWalk);
      raw = Math.max(raw, fileRatio);
    }
  } else if (globalWalk > 0) {
    raw = globalTerminal / globalWalk;
  }

  const walkComplete = !walkStillRunning
    && (state.walkAllFoldersComplete
      || (globalWalk > 0 && globalTerminal >= globalWalk && !state.globalWalkGrowing));

  if (walkStillRunning || state.globalWalkGrowing) {
    raw = Math.min(raw, PROVISIONAL_PROGRESS_CAP);
  } else if (walkComplete && globalWalk > 0 && globalTerminal >= globalWalk) {
    raw = 1;
  } else {
    raw = Math.min(raw, 1);
  }

  if (globalDirect || walkStillRunning) {
    state.monotonicProgress = raw;
    return raw;
  }

  const next = Math.max(state.monotonicProgress, raw);
  state.monotonicProgress = next;
  return next;
}

function computeGlobalProgress(
  state: GlobalScanState,
  phase: string,
  globalWalk: number,
  globalTerminal: number,
  sourcesCompleted: number,
  walkStillRunning: boolean,
): number {
  return computeProgressRatio(
    state, phase, globalWalk, globalTerminal, sourcesCompleted, walkStillRunning,
  );
}

function buildAggregateStatusLine(
  state: GlobalScanState,
  aggregatePhase: ScanAggregatePhase,
  globalWalk: number,
  globalTerminal: number,
): string | null {
  const { sourcesTotal, activeTagSourceIndex, lastWalkSourceIndex } = state;
  const multi = sourcesTotal > 1;
  const provisionalTotal = !state.walkAllFoldersComplete
    && (state.globalWalkGrowing || lastWalkSourceIndex < sourcesTotal);
  const walkLabel = (n: number) => (provisionalTotal ? `~${n.toLocaleString()}` : n.toLocaleString());

  if (aggregatePhase === 'finishing') return 'Scanning — finalizing…';

  if (aggregatePhase === 'reading_tags') {
    const folder = state.progressMode === 'global_direct'
      ? (lastWalkSourceIndex || activeTagSourceIndex || 0)
      : (activeTagSourceIndex || lastWalkSourceIndex);
    if (multi && folder > 0) {
      const fileHint = globalWalk > 0
        ? ` · ${globalTerminal.toLocaleString()} of ${walkLabel(globalWalk)} files`
        : '';
      return `Scanning — Reading tags (folder ${folder}/${sourcesTotal})${fileHint}`;
    }
    if (globalWalk > 0) {
      return `Scanning — ${globalTerminal.toLocaleString()} of ${walkLabel(globalWalk)} · reading tags`;
    }
    return 'Scanning — reading tags';
  }

  if (aggregatePhase === 'walking') {
    const folder = lastWalkSourceIndex;
    if (multi && folder > 0) {
      const foundHint = globalWalk > 0 ? ` · ${walkLabel(globalWalk)} files found` : '';
      return `Scanning — Walking folders (folder ${folder}/${sourcesTotal})${foundHint}`;
    }
    if (globalWalk > 0) {
      return `Scanning — finding files… ${walkLabel(globalWalk)} found`;
    }
    return 'Scanning — finding files…';
  }

  return null;
}

function updateGlobalScanState(opId: string, p: Record<string, unknown>): GlobalScanState {
  const state = getGlobalScanState(opId);
  const phase = String(p.phase ?? '');
  const sourcesTotalRaw = typeof p.sources_total === 'number' ? p.sources_total : 0;
  if (sourcesTotalRaw > 0) state.sourcesTotal = sourcesTotalRaw;

  const sourcesTotal = state.sourcesTotal;
  const interleaved = sourcesTotal > 1;

  const isWalkLike = phase === 'walk'
    || phase === 'enumerating'
    || phase === 'discovering'
    || phase === 'reconciling';
  const isTagLike = phase === 'tag_read' || phase === 'scanning';
  const isWalkTerminal = phase === 'walk_complete'
    || (phase === 'done' && p.walk_finished === true);
  const isFinalize = phase === 'finalize' || phase === 'finalizing' || phase === 'scan_complete';

  if (isWalkLike) {
    const walkIdx = resolveWalkSourceIndex(p, sourcesTotal);
    if (walkIdx != null) state.lastWalkSourceIndex = walkIdx;
  }

  if (isWalkTerminal) {
    const walkIdx = resolveWalkSourceIndex(p, sourcesTotal) ?? state.lastWalkSourceIndex;
    const walkCount = perSourceWalkCount(p);
    if (walkIdx != null && walkCount != null && walkCount >= 0) {
      const prev = state.sourceSlots.get(walkIdx);
      state.sourceSlots.set(walkIdx, {
        walkFiles: Math.max(walkCount, prev?.walkFiles ?? 0),
        terminalFiles: prev?.terminalFiles ?? 0,
        tagComplete: prev?.tagComplete ?? false,
      });
      state.activeTagSourceIndex = walkIdx;
      state.lastWalkSourceIndex = walkIdx;
    }
    if (walkIdx != null && sourcesTotal > 0 && walkIdx >= sourcesTotal) {
      state.walkAllFoldersComplete = true;
      state.globalWalkGrowing = false;
    }
  }

  if (isTagLike) {
    state.hasSeenTagRead = true;
    const useGlobalDirect = state.progressMode === 'global_direct'
      || isOverlapGlobalTotalsShape(p, sourcesTotal);

    if (!useGlobalDirect) {
      const tagIdx = resolveTagSourceIndex(p, state, sourcesTotal);
      if (tagIdx != null) {
        state.activeTagSourceIndex = tagIdx;
        const walkCount = perSourceWalkCount(p);
        const terminalRaw = typeof p.terminal_files === 'number' && p.terminal_files >= 0
          ? p.terminal_files
          : (typeof p.current_file === 'number' && p.current_file >= 0 ? p.current_file : null);
        const prev = state.sourceSlots.get(tagIdx);
        const walkFiles = walkCount ?? prev?.walkFiles ?? 0;
        const terminalFiles = terminalRaw ?? prev?.terminalFiles ?? 0;
        const tagComplete = walkFiles > 0 && terminalFiles >= walkFiles;
        state.sourceSlots.set(tagIdx, {
          walkFiles: Math.max(walkFiles, prev?.walkFiles ?? 0),
          terminalFiles: Math.max(terminalFiles, prev?.terminalFiles ?? 0),
          tagComplete,
        });
        if (tagComplete) state.activeTagSourceIndex = 0;
      }
    } else {
      state.progressMode = 'global_direct';
    }
    const eventFps = typeof p.files_per_second === 'number' && p.files_per_second > 0
      ? p.files_per_second
      : null;
    if (eventFps != null) {
      state.smoothedTagFps = state.smoothedTagFps > 0
        ? state.smoothedTagFps * 0.6 + eventFps * 0.4
        : eventFps;
    }
  }

  const { walk: globalWalk, terminal: globalTerminal } = resolveGlobalTotals(state, p);
  const sourcesCompleted = countSourcesCompleted(state);
  const walkStillRunning = isWalkStillRunning(state, phase);

  if (isTagLike) updateSmoothedTagFps(state, globalTerminal);

  let aggregatePhase: ScanAggregatePhase = state.aggregatePhase;
  if (isFinalize) {
    aggregatePhase = 'finishing';
  } else if (state.progressMode === 'global_direct') {
    if (state.hasSeenTagRead || isTagLike) {
      if (!walkStillRunning && globalWalk > 0 && globalTerminal >= globalWalk) {
        aggregatePhase = 'finishing';
      } else {
        aggregatePhase = 'reading_tags';
      }
    } else if (isWalkLike || isWalkTerminal) {
      aggregatePhase = 'walking';
    }
  } else if (isTagLike || (interleaved && state.hasSeenTagRead && state.activeTagSourceIndex > 0)) {
    aggregatePhase = 'reading_tags';
  } else if (isWalkLike || isWalkTerminal || phase === 'walk_complete') {
    aggregatePhase = 'walking';
  } else if (interleaved && state.hasSeenTagRead && sourcesCompleted < sourcesTotal) {
    aggregatePhase = 'reading_tags';
  } else if (isWalkLike) {
    aggregatePhase = 'walking';
  }

  state.aggregatePhase = aggregatePhase;

  const instantEta = estimateInstantGlobalEtaSec(
    state, globalWalk, globalTerminal, walkStillRunning, sourcesCompleted,
  );
  smoothEta(state, instantEta, walkStillRunning);

  computeGlobalProgress(
    state, phase, globalWalk, globalTerminal, sourcesCompleted, walkStillRunning,
  );

  return state;
}

/**
 * Running total of files found across all NAS folders during walk.
 * Per-folder counts replace the display — this sums completed folders + current folder peak.
 */
export function updateWalkRunningTotal(
  opId: string,
  p: Record<string, unknown>,
): number | null {
  const phase = String(p.phase ?? '');
  const sourceIndex = typeof p.source_index === 'number' ? p.source_index : null;
  const sourcesTotal = typeof p.sources_total === 'number' ? p.sources_total : null;
  const multiSource = sourcesTotal != null && sourcesTotal > 1 && sourceIndex != null
    && !(phase === 'tag_read' || phase === 'scanning') && sourceIndex !== sourcesTotal;

  const perFolderCount = typeof p.audio_files === 'number' && p.audio_files > 0
    ? p.audio_files
    : (walkTracksFoundFromProgress(p)
      ?? (typeof p.current_file === 'number' && p.current_file > 0 ? p.current_file : null));

  const isFolderWalkTerminal = (phase === 'done' && p.walk_finished === true)
    || phase === 'walk_complete';
  const isWalkLike = phase === 'walk'
    || phase === 'enumerating'
    || phase === 'discovering'
    || phase === 'reconciling';

  if (!multiSource) {
    if (isWalkLike || isFolderWalkTerminal) return perFolderCount;
    return null;
  }

  const globalState = globalScanByOp.get(opId);
  if (globalState?.progressMode === 'global_direct') {
    if (isWalkLike || isFolderWalkTerminal || phase === 'tag_read' || phase === 'scanning') {
      const direct = globalState.lastDirectGlobalWalk;
      return direct > 0 ? direct : perFolderCount;
    }
    return null;
  }

  const state = getWalkAggregate(opId);
  const walkIdx = resolveWalkSourceIndex(p, sourcesTotal!) ?? sourceIndex;

  if (isFolderWalkTerminal && perFolderCount != null && walkIdx != null) {
    if (walkIdx > state.lastSealedSourceIndex) {
      state.completedSum += perFolderCount;
      state.lastSealedSourceIndex = walkIdx;
    }
    state.activeFolderPeak = 0;
    state.activeSourceIndex = walkIdx;
    return state.completedSum;
  }

  if (isWalkLike) {
    if (walkIdx != null && walkIdx > state.activeSourceIndex) {
      state.activeSourceIndex = walkIdx;
      state.activeFolderPeak = 0;
    }
    if (perFolderCount != null) {
      state.activeFolderPeak = Math.max(state.activeFolderPeak, perFolderCount);
    }
    return state.completedSum + state.activeFolderPeak;
  }

  return state.completedSum > 0 ? state.completedSum : null;
}

export function isTagReadAwaitingScanComplete(metrics: HonestScanMetrics, phase: string): boolean {
  if (phase === 'tag_read' || phase === 'finalize' || phase === 'scan_complete') return false;
  if (phase !== 'scanning' && phase !== 'tag_read') return false;
  const walk = metrics.globalWalkFilesFound ?? metrics.walkFilesFound;
  const terminal = metrics.globalTerminalFiles ?? metrics.terminalFiles;
  if (walk == null || terminal == null || walk <= 0) return false;
  if (terminal < walk) return false;
  if (metrics.pendingJobs != null && metrics.pendingJobs > 0) return false;
  return true;
}

export function parseHonestScanMetrics(p: Record<string, unknown>): HonestScanMetrics {
  const walkFilesFound = typeof p.walk_files_found === 'number' && p.walk_files_found >= 0
    ? p.walk_files_found
    : (typeof p.total_files === 'number' && p.total_files > 0 ? p.total_files : null);
  const terminalFiles = typeof p.terminal_files === 'number' && p.terminal_files >= 0
    ? p.terminal_files
    : (typeof p.current_file === 'number' && p.current_file >= 0 ? p.current_file : null);
  const pendingJobs = typeof p.pending_jobs === 'number' && p.pending_jobs >= 0
    ? p.pending_jobs
    : null;
  const unchangedCount = typeof p.skipped_count === 'number' && p.skipped_count >= 0
    ? p.skipped_count
    : null;
  const indexedCount = typeof p.indexed_count === 'number' && p.indexed_count >= 0
    ? p.indexed_count
    : null;

  const setAside = p.set_aside;
  let setAsideTotal = 0;
  if (setAside && typeof setAside === 'object') {
    const s = setAside as ScanSetAsideSummary;
    setAsideTotal = (s.unreadable ?? 0)
      + (s.tag_error ?? 0)
      + (s.timeout ?? 0)
      + (s.permission ?? 0)
      + (s.other ?? 0);
  }

  const currentPath = typeof p.current_path === 'string' && p.current_path.trim()
    ? p.current_path.trim()
    : null;

  const hasHonestDenominator = walkFilesFound != null
    && walkFilesFound > 0
    && terminalFiles != null;

  return {
    walkFilesFound,
    terminalFiles,
    pendingJobs,
    setAsideTotal,
    indexedCount,
    unchangedCount,
    currentPath,
    hasHonestDenominator,
    walkRunningTotal: null,
    globalWalkFilesFound: null,
    globalTerminalFiles: null,
    sourcesTotal: null,
    sourcesCompleted: null,
    activeSourceIndex: null,
    aggregatePhase: 'idle',
    aggregateStatusLine: null,
    interleavedMultiSource: false,
    smoothedTagFps: null,
    globalEtaSec: null,
  };
}

export function enrichHonestScanMetrics(
  opId: string,
  p: Record<string, unknown>,
  base: HonestScanMetrics,
): HonestScanMetrics {
  const walkRunningTotal = updateWalkRunningTotal(opId, p);
  const globalState = updateGlobalScanState(opId, p);

  const globalDirect = globalState.progressMode === 'global_direct';
  let globalWalk = globalDirect
    ? globalState.lastDirectGlobalWalk
    : sumGlobalWalk(globalState);
  let globalTerminal = globalDirect
    ? globalState.lastDirectGlobalTerminal
    : sumGlobalTerminal(globalState);

  const peak = globalState.peakEventGlobalWalk;
  if (!globalDirect && peak > 0 && globalWalk > peak * GLOBAL_TOTALS_SANITY_MULTIPLIER) {
    globalWalk = globalState.lastDirectGlobalWalk || peak;
    globalTerminal = globalState.lastDirectGlobalTerminal;
  }

  const sourcesCompleted = countSourcesCompleted(globalState);

  const phaseStr = String(p.phase ?? '');
  const walkStillRunning = isWalkStillRunning(globalState, phaseStr);
  const globalEtaEstimating = isGlobalEtaMisleading(
    globalState,
    globalWalk,
    globalTerminal,
    walkStillRunning,
    globalState.smoothedEtaSec,
  );

  const aggregateStatusLine = buildAggregateStatusLine(
    globalState,
    globalState.aggregatePhase,
    globalWalk,
    globalTerminal,
  );

  const activeFolder = globalDirect
    ? (globalState.lastWalkSourceIndex > 0
      ? globalState.lastWalkSourceIndex
      : (globalState.activeTagSourceIndex > 0 ? globalState.activeTagSourceIndex : null))
    : (globalState.activeTagSourceIndex > 0
      ? globalState.activeTagSourceIndex
      : (globalState.lastWalkSourceIndex > 0 ? globalState.lastWalkSourceIndex : null));

  return {
    ...base,
    walkRunningTotal,
    globalWalkFilesFound: globalWalk > 0 ? globalWalk : null,
    globalTerminalFiles: globalTerminal > 0 || globalWalk > 0 ? globalTerminal : null,
    sourcesTotal: globalState.sourcesTotal > 0 ? globalState.sourcesTotal : null,
    sourcesCompleted: sourcesCompleted > 0 ? sourcesCompleted : null,
    activeSourceIndex: activeFolder,
    aggregatePhase: globalState.aggregatePhase,
    aggregateStatusLine,
    interleavedMultiSource: globalState.sourcesTotal > 1,
    globalDirectTotals: globalDirect || undefined,
    walkStillRunning: walkStillRunning || undefined,
    globalEtaEstimating: globalEtaEstimating || undefined,
    smoothedTagFps: globalState.smoothedTagFps > 0 ? globalState.smoothedTagFps : null,
    globalEtaSec: globalEtaEstimating ? null : globalState.smoothedEtaSec,
    hasHonestDenominator: (globalWalk > 0 && globalTerminal >= 0)
      || base.hasHonestDenominator,
    walkFilesFound: globalWalk > 0 ? globalWalk : base.walkFilesFound,
    terminalFiles: globalWalk > 0 ? globalTerminal : base.terminalFiles,
  };
}

export function classifyScanPhaseKind(phase: string): ScanPhaseKind {
  if (
    phase === 'walk'
    || phase === 'enumerating'
    || phase === 'discovering'
    || phase === 'reconciling'
    || phase === 'walk_complete'
  ) {
    return 'walking';
  }
  if (phase === 'tag_read' || phase === 'scanning') return 'reading_tags';
  if (phase === 'finalize' || phase === 'scan_complete' || phase === 'finalizing') return 'finishing';
  return 'other';
}

export function scanPhaseDisplayName(kind: ScanPhaseKind): string {
  switch (kind) {
    case 'walking': return 'walking folders';
    case 'reading_tags': return 'reading tags';
    case 'finishing': return 'finishing';
    default: return 'scanning';
  }
}

/** Percent in [0, 1] — global_direct uses provisional totals; no latch while walk runs. */
export function computeHonestScanProgress(
  opId: string,
  metrics: HonestScanMetrics,
  phase: string,
): number | undefined {
  const state = globalScanByOp.get(opId);
  const walkStillRunning = metrics.walkStillRunning
    ?? (state ? isWalkStillRunning(state, phase) : false);

  if (phase === 'done' || phase === 'scan_complete') return 1;

  if (state) {
    const walk = metrics.globalWalkFilesFound ?? metrics.walkFilesFound ?? 0;
    const terminal = metrics.globalTerminalFiles ?? metrics.terminalFiles ?? 0;
    const sourcesCompleted = metrics.sourcesCompleted ?? countSourcesCompleted(state);
    return computeProgressRatio(
      state, phase, walk, terminal, sourcesCompleted, walkStillRunning,
    );
  }

  if (phase === 'finalize' || phase === 'finalizing') {
    return walkStillRunning ? PROVISIONAL_PROGRESS_CAP : 0.98;
  }

  const walk = metrics.globalWalkFilesFound ?? metrics.walkFilesFound;
  const terminal = metrics.globalTerminalFiles ?? metrics.terminalFiles;
  if (walk != null && walk > 0 && terminal != null) {
    let raw = terminal / walk;
    if (walkStillRunning) {
      raw = Math.min(raw, PROVISIONAL_PROGRESS_CAP);
    } else if (terminal >= walk) {
      raw = 1;
    } else {
      raw = Math.min(raw, 1);
    }
    return raw;
  }

  return undefined;
}

export function formatScanningStatusLine(
  metrics: HonestScanMetrics,
  phase: string,
): string | null {
  if (metrics.aggregateStatusLine) return metrics.aggregateStatusLine;

  const kind = classifyScanPhaseKind(phase);

  if (kind === 'walking') {
    const n = metrics.walkRunningTotal ?? metrics.globalWalkFilesFound;
    if (n != null && n > 0) {
      return `Scanning — finding files… ${n.toLocaleString()} found`;
    }
    return 'Scanning — finding files…';
  }

  if (phase === 'finalize' || phase === 'finalizing') {
    return 'Scanning — finalizing…';
  }

  const walk = metrics.globalWalkFilesFound ?? metrics.walkFilesFound;
  const terminal = metrics.globalTerminalFiles ?? metrics.terminalFiles;
  if (walk != null && walk > 0 && terminal != null) {
    const done = terminal.toLocaleString();
    const total = walk.toLocaleString();
    if (kind === 'reading_tags') {
      return `Scanning — ${done} of ${total} · reading tags`;
    }
    if (kind === 'finishing' || phase === 'scan_complete') {
      return 'Scanning — finalizing…';
    }
    return `Scanning — ${done} of ${total}`;
  }

  if (kind === 'reading_tags') return 'Scanning — reading tags';
  if (kind === 'finishing') return 'Scanning — finalizing…';
  return null;
}

export function formatScanCompleteSummary(
  metrics: HonestScanMetrics,
  scanMode?: 'incremental' | 'cold' | null,
): string {
  const indexed = metrics.indexedCount
    ?? metrics.globalTerminalFiles
    ?? metrics.terminalFiles
    ?? metrics.globalWalkFilesFound
    ?? metrics.walkFilesFound
    ?? 0;
  const unchanged = metrics.unchangedCount ?? 0;
  const modeHint = scanMode === 'incremental'
    ? ' (incremental)'
    : scanMode === 'cold'
      ? ' (full scan)'
      : '';
  const parts = [`Scan complete — ${indexed.toLocaleString()} indexed${modeHint}`];
  if (metrics.setAsideTotal > 0) {
    const n = metrics.setAsideTotal.toLocaleString();
    parts.push(`${n} file${metrics.setAsideTotal === 1 ? '' : 's'} need attention`);
  }
  if (unchanged > 0) {
    parts.push(`${unchanged.toLocaleString()} unchanged`);
  }
  return parts.join(', ');
}

export function humanScanSkipCategoryLabel(category: string): string {
  switch (category) {
    case 'unreadable': return 'Unreadable';
    case 'tag_error': return 'Tag problem';
    case 'timeout': return 'Timed out';
    case 'permission': return 'Permission';
    default: return 'Other';
  }
}

export function humanScanSkipReason(category: string, reasonCode?: string): string {
  switch (category) {
    case 'tag_error':
      return "Couldn't read this file's tags — it may be damaged or use an unusual format.";
    case 'unreadable':
      return "Couldn't open this file — it may be missing, moved, or not a supported audio format.";
    case 'timeout':
      return 'Reading this file took too long — it may be on a slow or sleeping drive.';
    case 'permission':
      return "Maestro couldn't access this file — check folder permissions on your library drive.";
    case 'other':
      if (reasonCode === 'reconciliation_gap') {
        return "This file was found during the scan but didn't finish indexing — try Retry.";
      }
      return "This file couldn't be indexed — you can retry or remove it from the library folder.";
    default:
      return "This file couldn't be indexed during the scan.";
  }
}

export function basenameFromPath(filePath: string): string {
  const normalized = filePath.replace(/\\/g, '/');
  const parts = normalized.split('/').filter(Boolean);
  return parts[parts.length - 1] || filePath;
}
